/**
 * ID / 序号 / 游标的安全处理。
 *
 * 客服协议规定所有 ID、版本、序号、游标都是十进制字符串或 UUID，绝不使用 JS Number，
 * 因为服务端存在超出 2^53 的整型主键。这里集中处理"规范化十进制字符串"的判定与比较，
 * 避免各处自行用字符串排序或 Number() 转换导致精度丢失与错序。
 */

/** 规范化十进制：不含符号、不含前导零（除 "0" 本身）。 */
const CANONICAL_DECIMAL = /^(?:0|[1-9]\d*)$/u;

/** UUID v4（小写）形式，SDK/客户端自生成的实例标识使用。 */
const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/u;

/** 是否为规范化十进制字符串（非负整数，无前导零）。 */
export function isCanonicalDecimal(value: unknown): value is string {
  return typeof value === 'string' && CANONICAL_DECIMAL.test(value);
}

/**
 * 仅判定"十进制字符串"，允许前导零这类非规范化但可解析的输入。
 * 用于宽容校验外部输入，随后必须经 {@link normalizeDecimal} 规范化再入库或比较。
 */
export function isDecimalLike(value: unknown): value is string {
  return typeof value === 'string' && /^\d+$/u.test(value);
}

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID_V4.test(value);
}

/** 把十进制字符串规范化为无前导零形式；非法输入抛错，不做静默兜底。 */
export function normalizeDecimal(value: string): string {
  if (!isDecimalLike(value)) {
    throw new TypeError(`不是合法的十进制字符串: ${JSON.stringify(value)}`);
  }
  const stripped = value.replace(/^0+(?=\d)/u, '');
  return stripped;
}

/**
 * 比较两个十进制字符串。
 * 返回 -1 / 0 / 1；输入非十进制字符串时抛错（不 fallback 到字符串排序）。
 */
export function compareDecimal(left: string, right: string): -1 | 0 | 1 {
  const a = BigInt(normalizeDecimal(left));
  const b = BigInt(normalizeDecimal(right));
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

/** 十进制字符串是否落在 [min, max] 闭区间内（两端可为 null 表示不限制）。 */
export function isWithinDecimalRange(
  value: string,
  min: null | string,
  max: null | string,
): boolean {
  if (min !== null && compareDecimal(value, min) < 0) return false;
  if (max !== null && compareDecimal(value, max) > 0) return false;
  return true;
}

/** 生成随机的 pageInstanceId（每个标签页/实例一个，用于隔离上下文）。 */
export function newPageInstanceId(): string {
  const webCrypto: undefined | Crypto = globalThis.crypto;
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }
  if (typeof webCrypto?.getRandomValues !== 'function') {
    // 不降级到 Math.random：实例标识必须不可预测，环境缺失时直接失败。
    throw new Error('当前环境缺少 Web Crypto，无法生成安全的 pageInstanceId');
  }
  const bytes = new Uint8Array(16);
  webCrypto.getRandomValues(bytes);
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** 生成随机 requestId（握手/请求关联标识）。 */
export function newRequestId(): string {
  return newPageInstanceId();
}